"use client";

import { ArrowDown, Square } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ConsultingPricingComparison() {
  const header = useRevealOnScroll<HTMLDivElement>();
  const grid = useRevealOnScroll<HTMLDivElement>();
  const footnote = useRevealOnScroll<HTMLDivElement>();
  const tiers = [{
    label: "TIER 01 — REVENUE AUDIT",
    price: "$1.5K",
    priceNote: "FLAT",
    description: "A comprehensive diagnostic of your current revenue performance.",
    features: ["OTA channel performance audit", "Rate parity health check", "Tech stack efficiency review", "Competitor pricing analysis", "Actionable growth roadmap", "1-hour strategy debrief"],
    recommended: false,
    cta: "BOOK AUDIT"
  }, {
    label: "TIER 02 — REVENUE MANAGEMENT",
    price: "$3K",
    priceNote: "MO/FLAT",
    description: "Ongoing revenue management to maximize your daily yield.",
    features: ["Dynamic pricing optimization", "OTA listing management", "Direct booking strategy", "Weekly performance reporting", "Tech stack implementation", "Unlimited strategy support"],
    recommended: true,
    cta: "START MANAGEMENT"
  }, {
    label: "TIER 03 — TECH TRANSFORMATION",
    price: "$5K+",
    priceNote: "FLAT",
    description: "Full-scale digital transformation for your hotel property.",
    features: ["PMS & Channel Manager setup", "Booking engine integration", "Digital marketing alignment", "Staff training & onboarding", "Revenue culture development", "Quarterly growth planning"],
    recommended: false,
    cta: "REQUEST PROPOSAL"
  }];
  const hazardStripe = {
    backgroundImage: "repeating-linear-gradient(45deg, var(--secondary) 0 10px, var(--background) 10px 20px)"
  };
  return <section data-section-id="1637" id="consultingpricingcomparison" className="relative bg-background text-foreground py-24 md:py-36 overflow-hidden">
      {}
      <div aria-hidden="true" className="pointer-events-none absolute right-4 top-12 md:right-12 md:top-20 font-default font-extrabold leading-none text-foreground/10 select-none" style={{
      fontSize: "clamp(12rem, 28vw, 22rem)"
    }}>
        04
      </div>

      <div className="relative mx-auto max-w-[1360px] px-[clamp(1rem,4vw,3rem)]">
        {}
        <div ref={header.ref} className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 transition-all duration-[520ms] ${header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
        transitionTimingFunction: "cubic-bezier(0.2, 0.8, 0.2, 1)"
      }}>
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-6">
              <span className="font-default text-xs font-semibold tracking-[0.18em] uppercase text-foreground/70">
                SERVICE PACKAGES
              </span>
              <Square className="size-2 fill-secondary text-secondary" aria-hidden="true" />
              <span className="font-default text-xs font-semibold tracking-[0.18em] uppercase text-foreground/70">
                TRANSPARENT PRICING
              </span>
            </div>
            <h2 className="font-default font-extrabold uppercase leading-[0.94] tracking-tight text-foreground" style={{
            fontSize: "clamp(2.25rem, 5.4vw, 4.75rem)"
          }}>
              NO HIDDEN FEES. NO SURPRISES. JUST{" "}
              <span className="text-secondary">PROVEN</span> RESULTS.
            </h2>
          </div>

          <div className="lg:col-span-5 flex flex-col justify-end">
            <p className="font-default text-sm leading-relaxed text-foreground/80 mb-6">
              At HotelRevBaBa, we believe in clear, flat-rate pricing for our
              consulting services. Whether you need a one-time audit or ongoing
              revenue management, you know exactly what you are paying for.
            </p>
            <Button asChild variant="outline" className="self-start font-default uppercase tracking-[0.06em] font-bold border-2 border-foreground bg-transparent text-foreground hover:bg-foreground hover:text-background rounded-md h-auto px-6 py-3">
              <Link to="/ContactPage">
                [ DOWNLOAD SERVICE BROCHURE <ArrowDown className="size-4 ml-1" /> ]
              </Link>
            </Button>
          </div>
        </div>

        {}
        <div ref={grid.ref} className={`mt-16 md:mt-24 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 transition-all duration-[520ms] delay-100 ${grid.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
        transitionTimingFunction: "cubic-bezier(0.2, 0.8, 0.2, 1)"
      }}>
          {tiers.map((tier, i) => <div data-index={i} key={i} className={`group relative bg-card text-card-foreground border-2 transition-all duration-[220ms] hover:-translate-y-0.5 ${tier.recommended ? "border-secondary/40 hover:border-secondary" : "border-secondary/30 hover:border-secondary"}`} style={{
          transitionTimingFunction: "cubic-bezier(0.2, 0.8, 0.2, 1)"
        }}>
              {}
              {tier.recommended && <div aria-hidden="true" className="absolute left-0 top-0 bottom-0 w-2" style={hazardStripe} />}

              {}
              {tier.recommended && <Badge className="absolute -top-3 left-6 bg-secondary text-secondary-foreground font-default text-xs font-semibold tracking-[0.12em] uppercase rounded-md px-3 py-1 border-0 flex items-center gap-1.5">
                  <Square className="size-2 fill-current" aria-hidden="true" />
                  MOST POPULAR
                </Badge>}

              {}
              <span aria-hidden="true" className="absolute top-2.5 left-2.5 size-1.5 rounded-md bg-foreground/40 transition-colors duration-[220ms] group-hover:bg-secondary" />
              <span aria-hidden="true" className="absolute top-2.5 right-2.5 size-1.5 rounded-md bg-foreground/40 transition-colors duration-[220ms] group-hover:bg-secondary" />
              <span aria-hidden="true" className="absolute bottom-2.5 left-2.5 size-1.5 rounded-md bg-foreground/40 transition-colors duration-[220ms] group-hover:bg-secondary" />
              <span aria-hidden="true" className="absolute bottom-2.5 right-2.5 size-1.5 rounded-md bg-foreground/40 transition-colors duration-[220ms] group-hover:bg-secondary" />

              <div className="p-7 md:p-8 pt-10 md:pt-12 flex flex-col h-full">
                {}
                <div className="font-default text-xs font-semibold tracking-[0.18em] uppercase text-foreground/70 mb-6">
                  {tier.label}
                </div>

                {}
                <div className="flex items-baseline gap-3 mb-3">
                  <span className="font-default font-extrabold uppercase leading-none tracking-tight text-foreground" style={{
                fontSize: "clamp(3rem, 5vw, 4.5rem)"
              }}>
                    {tier.price}
                  </span>
                  <span className="font-default text-xs font-semibold tracking-[0.18em] uppercase text-secondary">
                    {tier.priceNote}
                  </span>
                </div>

                <p className="text-sm text-foreground/70 mb-6 leading-relaxed">
                  {tier.description}
                </p>

                <Separator className="bg-foreground/20 mb-6" />

                {}
                <ul className="space-y-3 mb-8 flex-1">
                  {tier.features.map((feature, j) => <li data-index={j} key={j} className="flex items-start gap-3 text-sm text-foreground/90">
                      <Square className="size-3 fill-secondary text-secondary shrink-0 mt-1" aria-hidden="true" />
                      <span>{feature}</span>
                    </li>)}
                </ul>

                {}
                <Button asChild className="w-full font-default uppercase tracking-[0.06em] font-bold bg-secondary text-secondary-foreground hover:bg-secondary/90 rounded-md h-auto px-7 py-4 border-0" style={{
              boxShadow: "4px 4px 0px 0px var(--foreground)"
            }}>
                  <Link to="/ContactPage">{tier.cta}</Link>
                </Button>
              </div>
            </div>)}
        </div>

        {}
        <div ref={footnote.ref} className={`mt-12 md:mt-16 transition-all duration-[520ms] delay-200 ${footnote.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
        transitionTimingFunction: "cubic-bezier(0.2, 0.8, 0.2, 1)"
      }}>
          {}
          <div aria-hidden="true" className="h-[14px] w-full mb-6" style={hazardStripe} />
          <p className="font-default text-xs font-semibold tracking-[0.12em] uppercase text-foreground/80 flex items-start gap-2 flex-wrap">
            <Square className="size-2 fill-secondary text-secondary shrink-0 mt-1.5" aria-hidden="true" />
            <span>
              ALL PACKAGES INCLUDE: INITIAL REVENUE AUDIT, DEDICATED CONSULTANT,
              AND MONTHLY PERFORMANCE REVIEWS.
            </span>
          </p>
        </div>
      </div>
    </section>;
}